// src/views/saas/PlanosList.jsx
import React, { useState, useEffect, useCallback } from "react";
import saasService from "../../services/saasService";
import Banner from "../../components/ui/Banner";
import ConfirmModal from "../../components/ui/ConfirmModal";

const fmtM = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});

const CATEGORIAS = [
  "Geral","Cadastros","Contabilidade","Financeiro","Cobrança",
  "Reservas","Comunicação","Consumo","Relatórios","Portal","Portaria",
  "Configurações","Jobs",
];

export default function PlanosList() {
  const [planos, setPlanos]     = useState([]);
  const [modulos, setModulos]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [form, setForm] = useState({
    nmPlano:"", descricao:"", preco:"", periodo:"Mensal",
    maxCondominios:"1", maxUsuarios:"3", ativo:true,
    destaque:false, cor:"#1B3A6B", modulos:[]
  });
  const [banner, setBanner] = useState(null);
  const [confirmacao, setConfirmacao] = useState(null);
  const notify = (mensagem, tipo="info") => setBanner({ mensagem, tipo });
  const askConfirm = (opts) => new Promise(resolve => setConfirmacao({ ...opts, resolve }));

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [rp, rm] = await Promise.all([
        saasService.listarPlanos(),
        saasService.listarModulos(),
      ]);
      setPlanos(rp.data||[]);
      setModulos(rm.data||[]);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  // Trabalha sempre com nmModulo (string) — o que o backend persiste
  const toggleModulo = (nm) => {
    if (!nm || typeof nm !== "string") {
      console.warn("[PlanosList] toggleModulo chamado com nm inválido:", nm);
      return;
    }
    setForm(p => {
      // Sanitiza: remove undefined/null/duplicatas que possam ter ficado de estados anteriores
      const mods = Array.isArray(p.modulos)
        ? [...new Set(p.modulos.filter(x => typeof x === "string" && x))]
        : [];
      return {
        ...p,
        modulos: mods.includes(nm)
          ? mods.filter(m => m !== nm)
          : [...mods, nm]
      };
    });
  };

  const abrirEditar = (plano) => {
    setEditData(plano);
    setForm({
      nmPlano: plano.nmPlano, descricao: plano.descricao||"",
      preco: plano.preco, periodo: plano.periodo,
      maxCondominios: plano.maxCondominios, maxUsuarios: plano.maxUsuarios,
      ativo: !!plano.ativo, destaque: !!plano.destaque,
      cor: plano.cor||"#1B3A6B", modulos: plano.modulos||[]
    });
    setShowModal(true);
  };

  const handleSalvar = async () => {
    if (!form.nmPlano.trim()) { notify("Nome do plano é obrigatório.", "warning"); return; }
    setSaving(true);
    try {
      if (editData?.id) await saasService.atualizarPlano(editData.id, form);
      else              await saasService.criarPlano(form);
      setShowModal(false); carregar();
      notify(editData?.id?"Plano atualizado com sucesso.":"Plano criado com sucesso.", "success");
    } catch(e) { notify(e.message || "Erro ao salvar plano.", "danger"); }
    setSaving(false);
  };

  const handleDeletar = async (id, nome) => {
    const ok = await askConfirm({
      titulo:"Excluir plano",
      mensagem:`Confirma a exclusão do plano "${nome}"? Esta ação não pode ser desfeita.`,
      textoOk:"Excluir", perigo:true,
    });
    if (!ok) return;
    try {
      await saasService.deletarPlano(id);
      notify("Plano removido com sucesso.", "success");
      carregar();
    } catch(e) { notify(e.message || "Erro ao excluir plano.", "danger"); }
  };

  // Agrupa módulos por categoria (backend usa `categoria`)
  const modulosPorCategoria = CATEGORIAS.reduce((acc, g) => {
    acc[g] = modulos.filter(m => (m.categoria || m.grupo) === g);
    return acc;
  }, {});
  // Módulos que ficaram fora das categorias conhecidas
  const semCategoria = modulos.filter(m => {
    const c = m.categoria || m.grupo;
    return !c || !CATEGORIAS.includes(c);
  });
  if (semCategoria.length) modulosPorCategoria["Outros"] = semCategoria;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-box-seam me-2" style={{color:"var(--primary)",fontSize:22}}/>
            Planos SaaS
          </h1>
          <p className="page-subtitle">Gerencie os planos e módulos disponíveis</p>
        </div>
        <button className="btn btn-primary"
          onClick={()=>{setEditData(null);setForm({nmPlano:"",descricao:"",preco:"",periodo:"Mensal",maxCondominios:"1",maxUsuarios:"3",ativo:true,destaque:false,cor:"#1B3A6B",modulos:[]});setShowModal(true);}}>
          <i className="bi bi-plus-lg me-1"/>Novo Plano
        </button>
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:60}}><span className="spinner-border text-primary"/></div>
      ) : planos.length===0 ? (
        <div className="card" style={{textAlign:"center",padding:60,color:"var(--gray-400)"}}>
          <i className="bi bi-box-seam" style={{fontSize:48,display:"block",marginBottom:12}}/>
          <p>Nenhum plano cadastrado.</p>
          <button className="btn btn-primary btn-sm"
            onClick={()=>{setEditData(null);setForm({nmPlano:"",descricao:"",preco:"",periodo:"Mensal",maxCondominios:"1",maxUsuarios:"3",ativo:true,destaque:false,cor:"#1B3A6B",modulos:[]});setShowModal(true);}}>
            <i className="bi bi-plus-lg me-1"/>Criar primeiro plano
          </button>
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:20}}>
          {planos.map(p=>(
            <div key={p.id} className="card" style={{
              border:`2px solid ${p.destaque?"var(--primary)":"var(--gray-200)"}`,
              position:"relative", overflow:"visible"
            }}>
              {p.destaque && (
                <div style={{position:"absolute",top:-12,left:"50%",transform:"translateX(-50%)",
                  background:"var(--primary)",color:"#fff",fontSize:11,fontWeight:700,
                  padding:"3px 14px",borderRadius:20,whiteSpace:"nowrap"}}>
                  ⭐ Destaque
                </div>
              )}
              <div style={{padding:"20px 20px 14px",borderBottom:"1px solid var(--gray-100)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <div style={{width:12,height:12,borderRadius:"50%",background:p.cor||"#1B3A6B"}}/>
                      <span style={{fontWeight:700,fontSize:16,color:"var(--gray-800)"}}>{p.nmPlano}</span>
                    </div>
                    <div style={{fontSize:12,color:"var(--gray-500)"}}>{p.descricao||"—"}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:20,fontWeight:700,color:"var(--primary)"}}>{fmtM(p.preco)}</div>
                    <div style={{fontSize:11,color:"var(--gray-400)"}}>/{p.periodo==="Mensal"?"mês":p.periodo}</div>
                  </div>
                </div>
                <div style={{display:"flex",gap:16,marginTop:12,fontSize:12,color:"var(--gray-500)",flexWrap:"wrap"}}>
                  <span><i className="bi bi-buildings me-1"/>{p.maxCondominios>=999?"Ilimitado":`Até ${p.maxCondominios} cond.`}</span>
                  <span><i className="bi bi-people me-1"/>{p.maxUsuarios>=50?"Ilimitado":`Até ${p.maxUsuarios} usuários`}</span>
                  <span><i className="bi bi-puzzle me-1"/>{(p.modulos||[]).length} módulos</span>
                  {p.qtdClientes>0 && (
                    <span style={{color:"#1D4ED8",fontWeight:600}}>
                      <i className="bi bi-person-check me-1"/>{p.qtdClientes} cliente(s)
                    </span>
                  )}
                </div>
              </div>
              {/* Módulos resumidos */}
              <div style={{padding:"12px 20px",maxHeight:120,overflowY:"auto"}}>
                {(p.modulos||[]).length === 0 ? (
                  <span style={{fontSize:11,color:"var(--gray-400)"}}>Nenhum módulo selecionado.</span>
                ) : (
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                    {(p.modulos||[]).map(nm=>{
                      const mod = modulos.find(x=>(x.id||x.nmModulo||x.nmmodulo)===nm);
                      return (
                        <span key={nm} style={{fontSize:10,fontWeight:600,padding:"2px 8px",
                          borderRadius:10,background:"var(--bg-highlight)",color:"#1D4ED8"}}>
                          {mod?.label || nm}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div style={{padding:"10px 20px",borderTop:"1px solid var(--gray-100)",display:"flex",gap:6}}>
                <button className="btn btn-outline-secondary btn-sm flex-fill"
                  onClick={()=>abrirEditar(p)}>
                  <i className="bi bi-pencil me-1"/>Editar
                </button>
                <span style={{fontSize:11,padding:"4px 8px",borderRadius:6,
                  background:p.ativo?"#DCFCE7":"#FEE2E2",
                  color:p.ativo?"#166534":"#991B1B",display:"flex",alignItems:"center"}}>
                  {p.ativo?"Ativo":"Inativo"}
                </span>
                <button className="btn btn-outline-danger btn-sm"
                  onClick={()=>handleDeletar(p.id, p.nmPlano)}>
                  <i className="bi bi-trash3"/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Banner global de notificação */}
      {banner && (
        <div style={{
          position:"fixed", top:70, left:0, right:0,
          zIndex:1100, display:"flex", justifyContent:"center",
          pointerEvents:"none", padding:"0 16px"
        }}>
          <div style={{ maxWidth:520, width:"100%", pointerEvents:"auto" }}>
            <Banner tipo={banner.tipo} onClose={()=>setBanner(null)}>
              {banner.mensagem}
            </Banner>
          </div>
        </div>
      )}

      {/* Modal de confirmação global */}
      {confirmacao && (
        <ConfirmModal
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          textoOk={confirmacao.textoOk}
          perigo={confirmacao.perigo}
          onConfirm={()=>{ confirmacao.resolve(true); setConfirmacao(null); }}
          onCancel={()=>{ confirmacao.resolve(false); setConfirmacao(null); }}
        />
      )}

      {/* Modal Criar/Editar Plano */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{maxWidth:720,maxHeight:"90vh",overflow:"auto"}}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-box-seam me-2"/>
                {editData?.id?"Editar Plano":"Novo Plano"}
              </h5>
              <button className="btn-close" onClick={()=>setShowModal(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label">Nome do Plano *</label>
                  <input className="form-control" value={form.nmPlano}
                    onChange={e=>set("nmPlano",e.target.value)} placeholder="Ex: Profissional"/>
                </div>
                <div className="col-3">
                  <label className="form-label">Preço (R$)</label>
                  <input type="number" className="form-control" value={form.preco}
                    onChange={e=>set("preco",e.target.value)} step="0.01"/>
                </div>
                <div className="col-3">
                  <label className="form-label">Período</label>
                  <select className="form-select" value={form.periodo} onChange={e=>set("periodo",e.target.value)}>
                    {["Mensal","Trimestral","Semestral","Anual"].map(p=><option key={p}>{p}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Descrição</label>
                  <input className="form-control" value={form.descricao}
                    onChange={e=>set("descricao",e.target.value)}/>
                </div>
                <div className="col-3">
                  <label className="form-label">Máx. Condomínios</label>
                  <input type="number" className="form-control" value={form.maxCondominios}
                    onChange={e=>set("maxCondominios",e.target.value)}/>
                  <small style={{fontSize:11,color:"var(--gray-400)"}}>999 = ilimitado</small>
                </div>
                <div className="col-3">
                  <label className="form-label">Máx. Usuários</label>
                  <input type="number" className="form-control" value={form.maxUsuarios}
                    onChange={e=>set("maxUsuarios",e.target.value)}/>
                  <small style={{fontSize:11,color:"var(--gray-400)"}}>50+ = ilimitado</small>
                </div>
                <div className="col-2">
                  <label className="form-label">Cor</label>
                  <input type="color" className="form-control form-control-color" value={form.cor}
                    onChange={e=>set("cor",e.target.value)} style={{height:38}}/>
                </div>
                <div className="col-2" style={{display:"flex",alignItems:"flex-end",gap:8,paddingBottom:4}}>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" checked={form.ativo}
                      onChange={e=>set("ativo",e.target.checked)}/>
                    <label className="form-check-label" style={{fontSize:13}}>Ativo</label>
                  </div>
                </div>
                <div className="col-2" style={{display:"flex",alignItems:"flex-end",gap:8,paddingBottom:4}}>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" checked={form.destaque}
                      onChange={e=>set("destaque",e.target.checked)}/>
                    <label className="form-check-label" style={{fontSize:13}}>Destaque</label>
                  </div>
                </div>

                {/* Módulos por grupo */}
                <div className="col-12">
                  <div style={{fontWeight:700,fontSize:13,color:"var(--gray-700)",
                    marginBottom:10,paddingTop:8,borderTop:"1px solid var(--gray-200)"}}>
                    Módulos Incluídos
                    <span style={{marginLeft:8,fontSize:11,color:"var(--gray-400)",fontWeight:400}}>
                      {(Array.isArray(form.modulos)?form.modulos:[]).length} selecionado(s)
                    </span>
                    <button type="button" className="btn btn-link btn-sm ms-2 p-0"
                      style={{fontSize:11}}
                      onClick={()=>setForm(p=>({...p,modulos:modulos.map(m=>m.id||m.nmModulo||m.nmmodulo).filter(Boolean)}))}>
                      Selecionar todos
                    </button>
                    <button type="button" className="btn btn-link btn-sm ms-1 p-0"
                      style={{fontSize:11,color:"#991B1B"}}
                      onClick={()=>setForm(p=>({...p,modulos:[]}))}>
                      Limpar
                    </button>
                  </div>
                  {Object.keys(modulosPorCategoria).map(g=>{
                    const modsGrupo = modulosPorCategoria[g]||[];
                    if (!modsGrupo.length) return null;
                    const selecionados = (Array.isArray(form.modulos)?form.modulos:[])
                      .filter(x => typeof x === "string" && x);
                    return (
                      <div key={g} style={{marginBottom:12}}>
                        <div style={{fontSize:11,fontWeight:700,color:"var(--gray-500)",
                          textTransform:"uppercase",letterSpacing:".5px",marginBottom:6}}>
                          {g}
                        </div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                          {modsGrupo.map((m, idx) => {
                            // A fonte canônica (`config/modulos.js`) usa `id`.
                            // Fallback para `nmModulo`/`nmmodulo` (origem antiga da tabela saas_modulos).
                            const nm = m.id || m.nmModulo || m.nmmodulo;
                            if (!nm) {
                              console.warn("[PlanosList] módulo sem identificador:", m);
                              return null;
                            }
                            const sel = selecionados.includes(nm);
                            return (
                              <button key={`${g}-${m.id || nm}-${idx}`} type="button"
                                onClick={()=>toggleModulo(nm)}
                                style={{
                                  fontSize:12,padding:"4px 12px",borderRadius:20,cursor:"pointer",
                                  border:`1px solid ${sel?"var(--primary)":"var(--gray-200)"}`,
                                  background:sel?"var(--primary)":"#fff",
                                  color:sel?"#fff":"var(--gray-600)",
                                  fontWeight:sel?700:400, transition:"all .15s"
                                }}>
                                {m.label || nm}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSalvar} disabled={saving}>
                {saving?<><span className="spinner-border spinner-border-sm me-1"/>Salvando...</>:"Salvar Plano"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
